import { CheckIcon } from '@heroicons/react/solid'
import Container from 'components/landing/Container'
import { FC } from 'lib/component-utils'
import { useIntl } from 'lib/intl-utils'

const contentByLang = {
  ru: {
    eyebrow: 'Всё, что нужно',
    heading: 'Для дистрибьюторов',
    description: 'Управляйте ассортиментом, осваивайте новые ниши',
    features: [
      {
        name: 'Анализ объёма продаж',
        description: 'в любых категориях с целью поиска и оценки возможностей роста вашего бизнеса',
      },
      {
        name: 'Управление ассортиментной матрицей и стоками',
        description: 'на основе анализа больших данных за продолжительный период',
      },
      {
        name: 'Автоматические ответы на отзывы',
        description: 'с подробной настройкой шаблонов, правил и стоп-слов',
      },
      {
        name: 'Анализ конкурентов',
        description: 'в разрезе ассортиментной матрицы и стратегий ценообразования',
      },
    ],
  },
  en: {
    eyebrow: 'All you need',
    heading: 'For distributors',
    description: 'Control your product range, expand to new niches',
    features: [
      {
        name: 'Sales volume analysis',
        description: 'in all categories to look for expansion opportunities',
      },
      {
        name: 'Product matrix and stock control',
        description: 'based on big data analysis over a long period',
      },
      {
        name: 'Review response automation',
        description: 'with detailed template, rule and stopword settings',
      },
      {
        name: 'Competitor analysis',
        description: 'by product matrix and pricing strategy',
      },
    ],
  },
} as const

const ForDistributors: FC = () => {
  const { t, lang } = useIntl()
  const content = contentByLang[lang as 'en' | 'ru']

  return (
    <Container className="!pt-px relative">
      <div className="grid grid-cols-1 gap-x-8 gap-y-16 mx-auto max-w-2xl sm:gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-2">
        <div className="lg:ml-auto lg:pl-4 lg:pt-4 lg:pr-8">
          <div className="lg:max-w-lg">
            <h2 className="text-base font-semibold leading-7 text-primary-600">
              {content.eyebrow}
            </h2>
            <p className="mt-2 text-3xl font-semibold text-gray-900 sm:text-4xl">
              {content.heading}
            </p>
            <p className="mt-6 text-lg leading-8 text-gray-600">{content.description}</p>
            <dl className="mt-10 space-y-8 max-w-xl text-base leading-7 text-gray-600 lg:max-w-none">
              {content.features.map((feature) => (
                <div key={feature.name} className="relative pl-9">
                  <dt className="inline font-medium text-gray-900">
                    <CheckIcon
                      className="absolute top-1 left-1 w-5 h-5 text-primary-500"
                      aria-hidden="true"
                    />
                    {feature.name}
                  </dt>{' '}
                  <dd className="inline">{feature.description}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
        <div className="flex justify-end items-start lg:order-first">
          <img
            src={`/next/img/landing/sellematics/${lang}/products.jpg`}
            alt=""
            className="w-[48rem] max-w-none rounded-xl shadow-xl ring-1 z-20 ring-gray-400/10 sm:w-[57rem]"
            width={2432}
            height={1442}
            loading="lazy"
          />
        </div>
      </div>
      <div
        className="hidden overflow-hidden absolute inset-x-0 -top-40 z-10 blur-3xl transform-gpu sm:block sm:-top-50"
        aria-hidden="true"
      >
        <div
          className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-[#ff5a39] to-[#fcc63c] opacity-20 sm:left-[calc(50%-35rem)] sm:w-[72.1875rem]"
          style={{
            clipPath:
              'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
          }}
        />
      </div>
    </Container>
  )
}

export default ForDistributors
